<template>
  <div id="app">
    <!-- Mobile Menu Toggle Button -->
    <button
      v-if="!isLoginRoute"
      class="mobile-menu-toggle"
      aria-label="Toggle menu"
      @click="toggleMobileMenu"
    >
      <span class="icon">
        <i class="mdi mdi-menu mdi-24px" />
      </span>
    </button>

    <!-- Mobile Overlay -->
    <div
      v-if="!isLoginRoute && isMobileMenuExpanded"
      class="mobile-overlay"
      @click="closeMobileMenu"
    />

    <aside-menu
      v-if="!isLoginRoute"
      :menu="menuItems"
    />
    <div class="main-content">
      <router-view @reload-menu="loadMenu" />
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import axios from 'axios'
import { API_ROUTES } from '@/axios'
import menuModule from '@/menu.js'
import AsideMenu from '@/components/AsideMenu.vue'

export default defineComponent({
  name: 'AppComponent',
  components: {
    AsideMenu
  },
  data () {
    return {
      menuItems: [] // Initialize as empty array
    }
  },
  computed: {
    isLoginRoute () {
      return this.$route.name === 'login'
    },
    isMobileMenuExpanded () {
      return this.$store.state.isAsideMobileExpanded
    }
  },
  watch: {
    // App es crea un sol cop, aixi que en fer login el created ja ha passat:
    // enganxem la transicio des de la pantalla d'entrada.
    $route (to, from) {
      if (from.name === 'login' && to.name !== 'login') {
        this.comprovarRendiment()
      }
    }
  },
  async created () {
    try {
      await this.$store.dispatch('fetchCurrentUser')
      await this.$store.dispatch('loadUserTheme')
      await this.$store.dispatch('loadUserLanguage')
      await this.loadMenu()
      this.comprovarRendiment()
    } catch (error) {
      console.error('Error initializing app:', error)
    }
  },
  methods: {
    async comprovarRendiment () {
      // Nomes un cop per sessio de navegador: si no, l'avis tornaria a sortir a
      // cada recarrega de la pagina i acabaria essent soroll.
      if (sessionStorage.getItem('avisRendimentMostrat')) {
        return
      }

      const token = localStorage.getItem('token')
      if (!token || this.$route.name === 'login') {
        return
      }

      try {
        // No cal comprovar el rol a part: l'endpoint ja exigeix superusuari i
        // respon 403/404 a qualsevol altre, cosa que aqui simplement ignorem.
        const { data } = await axios.get(API_ROUTES.MONITORING_DIMENSIONAMENT, {
          headers: { Authorization: `Bearer ${token}` }
        })

        const resum = data.resum || {}
        if (!resum.num_problemes) {
          return
        }

        const afectades = [
          ...(data.variables || []),
          ...((data.maquinari && data.maquinari.variables) || [])
        ].filter(v => v.estat === 'avis' || v.estat === 'critic')

        sessionStorage.setItem('avisRendimentMostrat', '1')

        const critic = resum.num_critics > 0
        const encapcalament = critic
          ? this.$t('conexions.monAlertaCritic')
          : this.$t('conexions.monAlertaMissatge')
        const llista = afectades.map(v => `<li>${v.etiqueta}</li>`).join('')

        this.$buefy.dialog.confirm({
          title: this.$t('conexions.monAlertaTitol'),
          message: `<p>${encapcalament}</p>` +
            `<p><strong>${this.$t('conexions.monAlertaAfectats')}:</strong></p>` +
            `<ul class="avis-rendiment-llista">${llista}</ul>`,
          confirmText: this.$t('conexions.monAlertaVeure'),
          cancelText: this.$t('conexions.monAlertaMesTard'),
          type: critic ? 'is-danger' : 'is-warning',
          hasIcon: true,
          onConfirm: () => {
            if (this.$route.path !== '/conexions') {
              this.$router.push('/conexions')
            }
          }
        })
      } catch (error) {
        // Sense permis, sense xarxa o backend caigut: l'avis no ha de molestar
      }
    },
    async loadMenu () {
      try {
        this.menuItems = []
        this.menuItems = await menuModule.getMenu()
      } catch (error) {
        //
      }
    },
    toggleMobileMenu () {
      this.$store.commit('asideMobileStateToggle')
    },
    closeMobileMenu () {
      this.$store.commit('asideMobileStateToggle', false)
    }
  }
})
</script>

<style>
#app {
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
}

/* Mobile Menu Toggle Button */
.mobile-menu-toggle {
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 50;
  background: var(--color-principal, #363636);
  border: none;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-menu-toggle:hover {
  background: var(--color-principal-hover, #4a4a4a);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.mobile-menu-toggle:active {
  transform: scale(0.95);
}

.mobile-menu-toggle .icon {
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Mobile Overlay */
.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 35;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Hide mobile toggle on desktop */
@media screen and (min-width: 1024px) {
  .mobile-menu-toggle {
    display: none;
  }
}

/* Show mobile toggle only on touch devices */
@media screen and (max-width: 1023px) {
  .mobile-menu-toggle {
    display: flex;
  }
}
</style>
